import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import confetti from 'canvas-confetti';
import Header from '../../components/ui/Header';
import ProjectBasicsForm from './components/ProjectBasicsForm';
import DynamicSpecsForm from './components/DynamicSpecsForm';
import FlightSearchStep from './components/FlightSearchStep';
import PhotoUpload from './components/PhotoUpload';
import VendorManagement from './components/VendorManagement';
import OptionsManagement from './components/OptionsManagement';
import QuoteCustomization from './components/QuoteCustomization';
import ProjectSummary from './components/ProjectSummary';
import StepNavigation from './components/StepNavigation';
import Icon from '../../components/AppIcon';

import { projectService } from '../../services/projectService';
import { vendorService } from '../../services/vendorService';
import { quoteService } from '../../services/quoteService';
import { trackEvent } from '../../hooks/useGoogleAnalytics';

import { sendVendorQuoteRequestEmailDetailed } from '../../services/emailService';
import { ToastContainer, useToast } from '../../components/ui/Toast';


const CreateProjectFlow = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editProjectId = searchParams?.get('edit');
  const isEditMode = !!editProjectId;
  const { toasts, addToast, removeToast } = useToast();

  const [currentStep, setCurrentStep] = useState(0);
  const [isCreating, setIsCreating] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [draftSaveStatus, setDraftSaveStatus] = useState(null);
  const [draftProjectId, setDraftProjectId] = useState(null);
  const [errors, setErrors] = useState({});
  const [visitedSteps, setVisitedSteps] = useState(new Set([0])); // Track visited steps
  const [isLoadingEdit, setIsLoadingEdit] = useState(isEditMode);
  const [isDraftEditMode, setIsDraftEditMode] = useState(false);
  const [autoOpenAssistant, setAutoOpenAssistant] = useState(false);

  const [formData, setFormData] = useState({
    basics: {
      purchaseType: '',
      projectName: '',
      firstName: '',
      budgetRange: '',
      timeline: '',
      zipCode: '',
      address: '',
      notes: '',
      isOnlineOnly: false
    },
    specs: {
      weightings: {} // Add weightings object to store field importance
    },
    photos: [], // Add photos array
    vendors: [],
    options: [],
    assistantConversation: null, // Persisted purchase assistant conversation
    quoteSettings: {
      deadline: '',
      customDeadline: '',
      requirements: [],
      instructions: '',
      allowPhoneCalls: false,
      allowSiteVisits: false,
      allowAlternatives: true,
      contactMethod: '',
      contactTime: ''
    }
  });

  // Dynamic steps based on online-only selection
  const getSteps = () => {
    const isOnlineOnly = formData?.basics?.isOnlineOnly;
    
    if (isOnlineOnly) {
      return [
        {
          title: 'Basics',
          description: 'Purchase details',
          icon: 'FileText'
        },
        {
          title: 'Needs',
          description: 'Product details',
          icon: 'Settings'
        },
        {
          title: 'Add Options',
          description: 'Purchase options',
          icon: 'ShoppingCart'
        },
        {
          title: 'Review & Create',
          description: 'Final review',
          icon: 'CheckCircle'
        }
      ];
    }
    
    return [
      {
        title: 'Basics',
        description: 'Purchase details',
        icon: 'FileText'
      },
      {
        title: 'Needs',
        description: 'Product details',
        icon: 'Settings'
      },
      {
        title: 'Upload Photos',
        description: 'Share images',
        icon: 'Image'
      },
      {
        title: 'Add Vendors',
        description: 'Vendor contacts',
        icon: 'Users'
      },
      {
        title: 'Quote Settings',
        description: 'Request details',
        icon: 'MessageSquare'
      },
      {
        title: 'Review & Create',
        description: 'Final review',
        icon: 'CheckCircle'
      }
    ];
  };

  const steps = getSteps();

  const updateFormData = (section, data) => {
    setFormData(prev => ({
      ...prev,
      [section]: data
    }));
    
    // Clear related errors when data is updated
    if (errors?.[section]) {
      setErrors(prev => ({
        ...prev,
        [section]: undefined
      }));
    }
    // Also clear deadline error when quoteSettings is updated
    if (section === 'quoteSettings' && errors?.deadline) {
      setErrors(prev => ({
        ...prev,
        deadline: undefined
      }));
    }
  };

  // Validate all required fields for final submission
  const validateForSubmission = () => {
    const newErrors = {};
    const isOnlineOnly = formData?.basics?.isOnlineOnly;

    // Always validate project basics for submission
    if (!formData?.basics?.purchaseType) {
      newErrors.purchaseType = 'Please select a purchase type';
    }
    if (!formData?.basics?.projectName?.trim()) {
      newErrors.projectName = 'Please enter a project name';
    }
    // Timeline only required for non-product (service) stacks
    if (!isOnlineOnly && !formData?.basics?.timeline) {
      newErrors.timeline = 'Please select a timeline';
    }
    // zipCode only required for non-online-only projects
    if (!isOnlineOnly && !formData?.basics?.zipCode?.trim()) {
      newErrors.zipCode = 'Please enter your zip code';
    }

    // Validate vendors or options based on project type
    if (isOnlineOnly) {
      // For Online Only projects, options are not required
    } else {
      if (!formData?.vendors || formData?.vendors?.length === 0) {
        newErrors.vendors = 'Please add at least one vendor';
      }
      
      // Validate quote settings for regular projects
      if (!formData?.quoteSettings?.deadline) {
        newErrors.deadline = 'Please select a response deadline';
      }
      if (formData?.quoteSettings?.deadline === 'custom' && !formData?.quoteSettings?.customDeadline) {
        newErrors.deadline = 'Please select a custom deadline date';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0 ? true : newErrors;
  };

  // Simple validation for step navigation (minimal requirements)
  const validateCurrentStep = () => {
    // No validation required for step navigation - users can move freely
    // This allows incomplete forms to progress through steps
    return true;
  };

  // New function to handle step navigation without validation restrictions
  const handleStepNavigation = (targetStep) => {
    // Allow navigation to any step (forward or backward)
    // Auto-open Purchase Assistant when navigating directly to Needs step (1)
    if (targetStep === 1 && currentStep !== 1) {
      setAutoOpenAssistant(true);
    } else {
      setAutoOpenAssistant(false);
    }
    setCurrentStep(targetStep);
    setVisitedSteps(prev => new Set([...prev, targetStep]));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSaveDraft = async () => {
    setIsSavingDraft(true);
    setDraftSaveStatus(null);

    try {
      // Build the category mapping (same as handleCreateProject)
      const categoryMap = {
        'car': 'cars',
        'home': 'home_improvement',
        'electronics': 'electronics',
        'furniture': 'furniture',
        'appliances': 'appliances',
      };
      const rawCategory = formData?.basics?.purchaseType?.toLowerCase()?.replace(/\s+/g, '_') || 'other';
      const category = categoryMap?.[rawCategory] || 'other';

      const projectPayload = {
        name: formData?.basics?.projectName || 'Untitled Stack',
        category,
        status: 'draft',
        description: formData?.basics?.notes || '',
        budgetMin: formData?.basics?.budgetRange ? parseFloat(formData?.basics?.budgetRange) : null,
        budgetMax: formData?.basics?.budgetRange ? parseFloat(formData?.basics?.budgetRange) : null,
        deadline: formData?.quoteSettings?.customDeadline || null,
        location: formData?.basics?.zipCode || '',
        specifications: {
          purchaseType: formData?.basics?.purchaseType || '',
          timeline: formData?.basics?.timeline || '',
          isOnlineOnly: formData?.basics?.isOnlineOnly || false,
          specs: formData?.specs || {},
          quoteSettings: formData?.quoteSettings || {},
          options: formData?.options || [],
          assistantConversation: formData?.assistantConversation || null,
          currentStep,
        },
        photos: formData?.photos || [],
      };

      let savedProjectId = null;

      // Determine whether to create or update in Supabase
      const existingId = isEditMode ? editProjectId : draftProjectId;

      if (existingId) {
        // Update existing project (edit mode or previously saved draft)
        await projectService?.update(existingId, projectPayload);
        savedProjectId = existingId;
      } else {
        // Create a new draft project in Supabase
        const created = await projectService?.create(projectPayload);
        savedProjectId = created?.id;
        setDraftProjectId(savedProjectId);
      }

      // Also keep localStorage as a fallback / restore point
      const draftData = {
        formData,
        currentStep,
        timestamp: Date.now(),
        lastModified: new Date()?.toISOString(),
        projectName: formData?.basics?.projectName || 'Untitled Project',
        isDraft: true,
        supabaseProjectId: savedProjectId,
      };
      localStorage.setItem('createProjectDraft', JSON.stringify(draftData));

      const existingDrafts = JSON.parse(localStorage.getItem('projectDrafts') || '[]');
      const updatedDrafts = existingDrafts?.filter(draft =>
        draft?.projectName !== draftData?.projectName
      );
      updatedDrafts?.push(draftData);
      localStorage.setItem('projectDrafts', JSON.stringify(updatedDrafts));

      setDraftSaveStatus('success');
      setTimeout(() => setDraftSaveStatus(null), 3000);

    } catch (error) {
      console.error('Error saving draft:', error);

      // Fallback: save to localStorage only
      try {
        const draftData = {
          formData,
          currentStep,
          timestamp: Date.now(),
          lastModified: new Date()?.toISOString(),
          projectName: formData?.basics?.projectName || 'Untitled Project',
          isDraft: true,
        };
        localStorage.setItem('createProjectDraft', JSON.stringify(draftData));
      } catch (localErr) {
        console.error('localStorage fallback also failed:', localErr);
      }

      setDraftSaveStatus('error');
      setTimeout(() => setDraftSaveStatus(null), 5000);
    } finally {
      setIsSavingDraft(false);
    }
  };

  const handleSaveAndExit = async () => {
    await handleSaveDraft();
    
    // Navigate to all projects with draft saved message
    navigate('/all-projects', { 
      state: { 
        message: `Draft "${formData?.basics?.projectName || 'Untitled Project'}" saved successfully`,
        type: 'success'
      }
    });
  };

  const handleNext = () => {
    // No validation for step progression - just move to next step
    if (currentStep < steps?.length - 1) {
      const nextStep = currentStep + 1;
      // Auto-open Purchase Assistant when moving from Basics (0) to Needs (1)
      if (currentStep === 0 && nextStep === 1) {
        setAutoOpenAssistant(true);
      } else {
        setAutoOpenAssistant(false);
      }
      setCurrentStep(nextStep);
      setVisitedSteps(prev => new Set([...prev, nextStep]));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      // Only validate when actually creating/submitting the project
      handleCreateProject();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleCancel = () => {
    navigate('/all-projects');
  };

  const handleEditStep = (stepIndex) => {
    setCurrentStep(stepIndex);
  };

  const handleCreateProject = async () => {
    // Validate all required fields before submission
    const validationResult = validateForSubmission();
    if (validationResult !== true) {
      // Redirect to the relevant step based on which errors exist
      const isOnlineOnly = formData?.basics?.isOnlineOnly;
      if (!isOnlineOnly) {
        if (validationResult?.vendors) {
          // Jump to Add Vendors step (index 3 in non-online flow)
          const vendorsStepIndex = steps?.findIndex(s => s?.title === 'Add Vendors');
          setCurrentStep(vendorsStepIndex >= 0 ? vendorsStepIndex : 3);
          window.scrollTo({ top: 0, behavior: 'smooth' });
          return;
        }
        if (validationResult?.deadline) {
          // Jump to Quote Settings step (index 4 in non-online flow)
          const quoteStepIndex = steps?.findIndex(s => s?.title === 'Quote Settings');
          setCurrentStep(quoteStepIndex >= 0 ? quoteStepIndex : 4);
          window.scrollTo({ top: 0, behavior: 'smooth' });
          return;
        }
      }
      return;
    }

    setIsCreating(true);

    try {
      const isOnlineOnly = formData?.basics?.isOnlineOnly;

      // Map category to valid enum value
      const categoryMap = {
        'car': 'cars',
        'cars': 'cars',
        'home': 'home_improvement',
        'home_improvement': 'home_improvement',
        'electronics': 'electronics',
        'furniture': 'furniture',
        'appliances': 'appliances',
      };
      const rawCategory = formData?.basics?.purchaseType?.toLowerCase()?.replace(/\s+/g, '_') || 'other';
      const category = categoryMap?.[rawCategory] || 'other';

      // If in edit mode for a NON-draft (active) project, just update and return
      if (isEditMode && editProjectId && !isDraftEditMode) {
        await projectService?.update(editProjectId, {
          name: formData?.basics?.projectName || 'Untitled Stack',
          category,
          description: formData?.basics?.notes || '',
          budgetMin: formData?.basics?.budget ? parseFloat(formData?.basics?.budget) : null,
          budgetMax: formData?.basics?.budget ? parseFloat(formData?.basics?.budget) : null,
          deadline: formData?.quoteSettings?.customDeadline || null,
          location: formData?.basics?.zipCode || '',
          specifications: {
            purchaseType: formData?.basics?.purchaseType || '',
            timeline: formData?.basics?.timeline || '',
            notes: formData?.basics?.notes || '',
            specs: formData?.specs || {},
            quoteSettings: formData?.quoteSettings || {},
            isOnlineOnly: formData?.basics?.isOnlineOnly || false,
            options: formData?.options || [],
            assistantConversation: formData?.assistantConversation || null,
          },
          photos: formData?.photos || []
        });

        trackEvent('stack_updated', {
          category: formData?.basics?.purchaseType || 'unknown',
          project_name: formData?.basics?.projectName || 'untitled',
          project_id: editProjectId,
        });

        navigate('/all-projects', {
          state: {
            message: `Stack "${formData?.basics?.projectName}" updated successfully!`,
            type: 'success',
            projectId: editProjectId,
          }
        });
        return;
      }

      // For draft edit mode: promote the existing draft project to active
      // OR create a brand new project (non-edit mode)
      let projectId;

      if (isEditMode && editProjectId && isDraftEditMode) {
        // Promote draft to active by updating the existing record
        await projectService?.update(editProjectId, {
          name: formData?.basics?.projectName || 'Untitled Stack',
          category,
          status: 'active',
          description: formData?.basics?.notes || '',
          budgetMin: formData?.basics?.budget ? parseFloat(formData?.basics?.budget) : null,
          budgetMax: formData?.basics?.budget ? parseFloat(formData?.basics?.budget) : null,
          deadline: formData?.quoteSettings?.customDeadline || null,
          location: formData?.basics?.zipCode || '',
          specifications: {
            purchaseType: formData?.basics?.purchaseType || '',
            timeline: formData?.basics?.timeline || '',
            notes: formData?.basics?.notes || '',
            isOnlineOnly: formData?.basics?.isOnlineOnly || false,
            specs: formData?.specs || {},
            quoteSettings: formData?.quoteSettings || {},
            options: formData?.options || [],
            assistantConversation: formData?.assistantConversation || null,
          },
          photos: formData?.photos || []
        });
        projectId = editProjectId;
      } else {
        // 1. Create the project in Supabase
        const createdProject = await projectService?.create({
          name: formData?.basics?.projectName || 'Untitled Stack',
          category,
          status: isOnlineOnly ? 'active' : 'active',
          description: formData?.basics?.notes || '',
          budgetMin: formData?.basics?.budget ? parseFloat(formData?.basics?.budget) : null,
          budgetMax: formData?.basics?.budget ? parseFloat(formData?.basics?.budget) : null,
          deadline: formData?.quoteSettings?.customDeadline || null,
          location: formData?.basics?.zipCode || '',
          specifications: {
            purchaseType: formData?.basics?.purchaseType || '',
            timeline: formData?.basics?.timeline || '',
            notes: formData?.basics?.notes || '',
            isOnlineOnly: formData?.basics?.isOnlineOnly || false,
            specs: formData?.specs || {},
            quoteSettings: formData?.quoteSettings || {},
            options: formData?.options || [],
            assistantConversation: formData?.assistantConversation || null,
          },
          photos: formData?.photos || []
        });
        projectId = createdProject?.id;
      }

      // 2. For online-only stacks: create vendor + quote rows for each option
      if (isOnlineOnly && formData?.options?.length > 0) {
        for (let index = 0; index < formData?.options?.length; index++) {
          const option = formData?.options?.[index];
          try {
            const price = option?.price ? parseFloat(String(option?.price)?.replace(/[^0-9.]/g, '')) : null;

            // Create or upsert a vendor row for this option
            let vendorRow;
            try {
              vendorRow = await vendorService?.create({
                name: option?.name || 'Unknown Vendor',
                email: `option-${projectId}-${index}-${Date.now()}@placeholder.com`,
                phone: '',
                website: option?.website || '',
                categories: [category],
                bio: 'Online Retailer'
              });
            } catch (vendorErr) {
              console.warn('Could not create vendor for option:', option?.name, vendorErr);
              continue;
            }

            // Link vendor to project
            try {
              await vendorService?.linkToProject(projectId, vendorRow?.id);
            } catch (linkErr) {
              console.warn('Could not link vendor to project:', linkErr);
            }

            // Build specs map from project-level specs (what the user is looking for)
            // This allows the comparison page to score each option against the project needs
            const projectSpecs = formData?.specs || {};
            const specsMap = {};
            Object.keys(projectSpecs)?.forEach(key => {
              if (key !== 'weightings' && projectSpecs?.[key] != null && projectSpecs?.[key] !== '') {
                specsMap[key] = projectSpecs?.[key];
              }
            });
            // Override priceMax with the actual option price if provided
            if (price != null) {
              specsMap['priceMax'] = price;
            }
            // Override destination with the option's own destination if captured from AI suggestion
            if (option?.destination) {
              specsMap['destination'] = option?.destination;
            }
            // Include review data from the option so the comparison table can display/score it
            if (option?.reviewScore != null) specsMap['reviewScore'] = option?.reviewScore;
            if (option?.reviewCount != null) specsMap['reviewCount'] = option?.reviewCount;
            if (option?.reviewMaxScore != null) specsMap['reviewMaxScore'] = option?.reviewMaxScore;
            // Include product image URL so the comparison table can display it
            if (option?.imageUrl) specsMap['imageUrl'] = option?.imageUrl;

            // Build a human-readable specifications array from the specs
            const specificationsArray = Object.entries(specsMap)
              ?.filter(([, v]) => v != null && String(v)?.trim() !== '')
              ?.map(([k, v]) => `${k}: ${v}`);
            if (option?.notes) specificationsArray?.push(option?.notes);

            // Create a quote row for this option
            await quoteService?.create({
              projectId,
              vendorId: vendorRow?.id,
              status: 'submitted',
              totalAmount: price,
              lineItems: {
                deliveryTime: '',
                specifications: specificationsArray,
                specs: specsMap,
                productUrl: option?.productUrl || ''
              },
              notes: option?.notes || ''
            });
          } catch (optionErr) {
            console.warn('Failed to save option to Supabase:', option?.name, optionErr);
          }
        }
      }

      // 3. For vendor-request stacks: link vendors and create pending quote rows
      if (!isOnlineOnly && formData?.vendors?.length > 0) {
        const emailFailedVendors = [];

        for (const vendor of formData?.vendors) {
          let vendorRow;
          try {
            vendorRow = await vendorService?.create({
              name: vendor?.name || vendor?.company || 'Unknown Vendor',
              email: vendor?.email || `${(vendor?.name || 'vendor')?.toLowerCase()?.replace(/\s+/g, '')}@placeholder.com`,
              phone: vendor?.phone || '',
              website: vendor?.website || '',
              categories: [category],
              bio: vendor?.notes || ''
            });
          } catch (vendorErr) {
            console.warn('Could not create vendor:', vendor?.name, vendorErr);
          }

          // If vendor creation failed, skip DB linking but still attempt email
          if (vendorRow?.id) {
            // Link vendor to project
            try {
              await vendorService?.linkToProject(projectId, vendorRow?.id);
            } catch (linkErr) {
              console.warn('Could not link vendor to project:', linkErr);
            }

            // Create a pending quote row
            try {
              await quoteService?.create({
                projectId,
                vendorId: vendorRow?.id,
                status: 'pending',
                totalAmount: null,
                lineItems: { specifications: [], specs: {} },
                notes: ''
              });
            } catch (quoteErr) {
              console.warn('Could not create quote row for vendor:', vendor?.name, quoteErr);
            }
          }

          // Always attempt to send email regardless of DB linking outcome
          if (vendor?.email && !vendor?.email?.includes('@placeholder.com')) {
            try {
              // Build budget range string
              const budgetVal = formData?.basics?.budgetRange || formData?.basics?.budget;
              const budgetRange = budgetVal ? `$${parseFloat(String(budgetVal)?.replace(/[^0-9.]/g, ''))?.toLocaleString()}` : null;

              // Build specs object (exclude weightings and empty values)
              const rawSpecs = formData?.specs || {};
              const cleanSpecs = Object.fromEntries(
                Object.entries(rawSpecs)?.filter(([k, v]) => k !== 'weightings' && v != null && String(v)?.trim() !== '')
              );

              // Get quotesheet if active
              const quoteSettings = formData?.quoteSettings || {};
              const quotesheet = quoteSettings?.quotesheetActive && quoteSettings?.quotesheet
                ? quoteSettings?.quotesheet
                : null;

              const emailResult = await sendVendorQuoteRequestEmailDetailed(vendor?.email, {
                vendorName: vendor?.name || vendor?.company || 'there',
                projectName: formData?.basics?.projectName || 'a project',
                buyerFirstName: formData?.basics?.firstName || 'A buyer',
                quoteRequestUrl: `https://mibuy2878.builtwithrocket.new/vendor-project-details?projectId=${projectId}&vendorName=${encodeURIComponent(vendor?.name || vendor?.company || '')}&vendorEmail=${encodeURIComponent(vendor?.email || '')}`,
                projectDescription: formData?.basics?.notes || '',
                budgetRange,
                timeline: formData?.basics?.timeline || '',
                location: formData?.basics?.zipCode || '',
                category: formData?.basics?.purchaseType || '',
                photos: formData?.photos || [],
                specs: cleanSpecs,
                quoteSettings: {
                  deadline: quoteSettings?.deadline || '',
                  customDeadline: quoteSettings?.customDeadline || '',
                  requirements: quoteSettings?.requirements || [],
                  instructions: quoteSettings?.instructions || '',
                },
                quotesheet,
              });

              if (!emailResult?.success) {
                console.warn('[create-project] Email failed for vendor:', vendor?.name, emailResult?.error);
                emailFailedVendors?.push(vendor?.name || vendor?.company || vendor?.email || 'Unknown vendor');
              }
            } catch (emailErr) {
              console.warn('Could not send quote request email to vendor:', vendor?.name, emailErr);
              emailFailedVendors?.push(vendor?.name || vendor?.company || vendor?.email || 'Unknown vendor');
            }
          }
        }

        // Show warning toast if any vendor emails failed
        if (emailFailedVendors?.length > 0) {
          setTimeout(() => {
            addToast(
              `Stack created, but email failed to send to: ${emailFailedVendors?.join(', ')}. Please verify their email addresses.`,
              'warning',
              6000
            );
          }, 1500);
        }
      }

      // Clear draft data after successful project creation
      localStorage.removeItem('createProjectDraft');
      const existingDrafts = JSON.parse(localStorage.getItem('projectDrafts') || '[]');
      const updatedDrafts = existingDrafts?.filter(draft =>
        draft?.projectName !== formData?.basics?.projectName
      );
      localStorage.setItem('projectDrafts', JSON.stringify(updatedDrafts));

      const optionsCount = isOnlineOnly ? (formData?.options?.length || 0) : (formData?.vendors?.length || 0);
      const successMessage = isOnlineOnly
        ? `Stack "${formData?.basics?.projectName}" created! ${optionsCount} option(s) saved for comparison.`
        : `Stack "${formData?.basics?.projectName}" created! Quote requests sent to ${optionsCount} vendor(s).`;

      trackEvent('stack_created', {
        category: formData?.basics?.purchaseType || 'unknown',
        project_name: formData?.basics?.projectName || 'untitled',
        options_count: optionsCount,
        is_online_only: isOnlineOnly,
      });

      // Fire celebratory confetti before navigating to comparison
      const duration = 1800;
      const end = Date.now() + duration;
      const colors = ['#6366f1', '#8b5cf6', '#a78bfa', '#c4b5fd', '#f9a8d4', '#fbbf24'];
      const frame = () => {
        confetti({
          particleCount: 4,
          angle: 60,
          spread: 55,
          origin: { x: 0 },
          colors,
        });
        confetti({
          particleCount: 4,
          angle: 120,
          spread: 55,
          origin: { x: 1 },
          colors,
        });
        if (Date.now() < end) {
          requestAnimationFrame(frame);
        }
      };
      frame();
      await new Promise(resolve => setTimeout(resolve, 900));

      navigate('/project-comparison?project=' + projectId, {
        state: {
          message: successMessage,
          type: 'success',
          projectStatus: 'active'
        }
      });
    } catch (error) {
      console.error('Error creating project:', error);
      // Surface the real error so it's easier to diagnose
      const detail = error?.message || error?.details || '';
      const userMessage = detail
        ? `Failed to create stack: ${detail}`
        : 'Failed to create stack. Please try again.';
      setErrors({ general: userMessage });
    } finally {
      setIsCreating(false);
    }
  };

  const getNextButtonText = () => {
    if (currentStep === steps?.length - 1) {
      if (isEditMode && !isDraftEditMode) return 'Save Changes';
      return formData?.basics?.isOnlineOnly ? 'Create Stack & Compare' : 'Create Stack & Send Requests';
    }
    return 'Next';
  };

  // Remove restrictions for next button - only disable for final submission if validation fails
  const isNextDisabled = () => {
    // Never disable next button for step navigation
    if (currentStep < steps?.length - 1) {
      return false;
    }
    
    // For final submission, we'll handle validation in handleCreateProject
    return false;
  };

  const renderCurrentStep = () => {
    const isOnlineOnly = formData?.basics?.isOnlineOnly;
    const isFlights = formData?.basics?.purchaseType === 'flights';
    
    switch (currentStep) {
      case 0:
        return (
          <ProjectBasicsForm
            formData={formData}
            updateFormData={updateFormData}
            errors={errors}
            onProductSubcategorySelected={() => {
              // Auto-advance to Needs step for product subcategories
              setAutoOpenAssistant(true);
              setCurrentStep(1);
              setVisitedSteps(prev => new Set([...prev, 1]));
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
          />
        );
      case 1:
        // If flights subcategory is selected, show the Duffel flight search instead of DynamicSpecsForm
        if (isFlights) {
          return (
            <FlightSearchStep
              formData={formData}
              updateFormData={updateFormData}
            />
          );
        }
        return (
          <DynamicSpecsForm
            formData={formData}
            updateFormData={updateFormData}
            errors={errors}
            autoOpen={autoOpenAssistant}
            onNeedsPopulated={() => {
              // Auto-advance to Add Options (step 2) when needs are populated
              setAutoOpenAssistant(false);
              setCurrentStep(2);
              setVisitedSteps(prev => new Set([...prev, 2]));
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
          />
        );
      case 2:
        // Online-only skips photos — go straight to Add Options
        if (isOnlineOnly) {
          return (
            <OptionsManagement
              formData={formData}
              updateFormData={updateFormData}
              errors={errors}
              onAddSingleOption={() => {}}
            />
          );
        }
        return (
          <PhotoUpload
            formData={formData}
            updateFormData={updateFormData}
            errors={errors}
          />
        );
      case 3:
        // Online-only: Review & Create. Regular: Add Vendors
        return isOnlineOnly ? (
          <ProjectSummary
            formData={formData}
            onEdit={handleEditStep}
            onCreateProject={handleCreateProject}
            isCreating={isCreating}
            errors={errors}
          />
        ) : (
          <VendorManagement
            formData={formData}
            updateFormData={updateFormData}
            errors={errors}
          />
        );
      case 4:
        // Regular projects only: Quote Settings
        return (
          <QuoteCustomization
            formData={formData}
            updateFormData={updateFormData}
            errors={errors}
          />
        );
      case 5:
        // Regular projects only: Review & Create
        return (
          <ProjectSummary
            formData={formData}
            onEdit={handleEditStep}
            onCreateProject={handleCreateProject}
            isCreating={isCreating}
            errors={errors}
          />
        );
      default:
        return null;
    }
  };

  // Reset visited steps when switching between online-only modes
  useEffect(() => {
    const isOnlineOnly = formData?.basics?.isOnlineOnly;
    const maxStep = isOnlineOnly ? 3 : 5; // Online-only has 4 steps (0-3), regular has 6 steps (0-5)
    
    if (currentStep > maxStep) {
      setCurrentStep(maxStep);
    }
    
    // Reset visited steps to only include current valid steps
    setVisitedSteps(prev => {
      const validSteps = new Set([...prev].filter(step => step <= maxStep));
      validSteps?.add(0); // Always include first step
      validSteps?.add(currentStep); // Always include current step
      return validSteps;
    });
  }, [formData?.basics?.isOnlineOnly, currentStep]);

  // Enhanced auto-save to localStorage including visited steps
  useEffect(() => {
    // Don't overwrite localStorage draft when in edit mode
    if (isEditMode) return;
    const saveData = {
      formData,
      currentStep,
      visitedSteps: Array.from(visitedSteps),
      timestamp: Date.now(),
      lastModified: new Date()?.toISOString(),
      projectName: formData?.basics?.projectName || 'Untitled Project',
      isDraft: true
    };
    localStorage.setItem('createProjectDraft', JSON.stringify(saveData));
  }, [formData, currentStep, visitedSteps]);

  // Load saved data on mount including visited steps
  useEffect(() => {
    // If in edit mode, load the existing project from Supabase
    if (isEditMode && editProjectId) {
      setIsLoadingEdit(true);
      projectService?.getById(editProjectId)?.then((project) => {
          const specs = project?.specifications || {};
          // Map category back to purchaseType
          const categoryReverseMap = {
            'cars': 'car',
            'home_improvement': 'home',
            'electronics': 'electronics',
            'furniture': 'furniture',
            'appliances': 'appliances',
          };
          const purchaseType = categoryReverseMap?.[project?.category] || project?.category || '';
          // Use the saved isOnlineOnly flag from specifications (most reliable source)
          const isOnlineOnly = specs?.isOnlineOnly === true;

          // Mark as draft edit mode so the submit button shows "Create Stack"
          if (project?.status === 'draft') {
            setIsDraftEditMode(true);
          }

          // Restore quoteSettings including quotesheet fields
          const savedQuoteSettings = specs?.quoteSettings || {};
          const restoredQuoteSettings = {
            deadline: savedQuoteSettings?.deadline || (project?.deadline ? 'custom' : ''),
            customDeadline: savedQuoteSettings?.customDeadline || project?.deadline || '',
            requirements: savedQuoteSettings?.requirements || [],
            instructions: savedQuoteSettings?.instructions || '',
            allowPhoneCalls: savedQuoteSettings?.allowPhoneCalls || false,
            allowSiteVisits: savedQuoteSettings?.allowSiteVisits || false,
            allowAlternatives: savedQuoteSettings?.allowAlternatives !== undefined ? savedQuoteSettings?.allowAlternatives : true,
            contactMethod: savedQuoteSettings?.contactMethod || '',
            contactTime: savedQuoteSettings?.contactTime || '',
            // Restore quotesheet fields
            quotesheetActive: savedQuoteSettings?.quotesheetActive || false,
            quotesheet: savedQuoteSettings?.quotesheet || null,
            quotesheetType: savedQuoteSettings?.quotesheetType || null,
          };

          setFormData({
            basics: {
              purchaseType: specs?.purchaseType || purchaseType,
              projectName: project?.name || '',
              firstName: specs?.firstName || '',
              budgetRange: specs?.budgetRange || (project?.budgetMin ? String(project?.budgetMin) : ''),
              timeline: specs?.timeline || '',
              zipCode: project?.location || '',
              address: project?.address || '',
              notes: project?.description || specs?.notes || '',
              isOnlineOnly,
            },
            specs: specs?.specs || { weightings: {} },
            photos: project?.photos || [],
            vendors: project?.vendors?.map(v => ({
              id: v?.id,
              name: v?.name || v?.company_name || '',
              email: v?.email || '',
              phone: v?.phone || '',
              website: v?.website || '',
              notes: v?.bio || '',
            })) || [],
            options: specs?.options || [],
            assistantConversation: specs?.assistantConversation || null,
            quoteSettings: restoredQuoteSettings,
          });
          setVisitedSteps(new Set([0, 1, 2, 3, 4, 5]));
        })?.catch((err) => {
          console.error('Error loading project for edit:', err);
          setErrors({ general: 'Failed to load project details. Please try again.' });
        })?.finally(() => {
          setIsLoadingEdit(false);
        });
      return; // Skip localStorage loading when in edit mode
    }

    const savedData = localStorage.getItem('createProjectDraft');
    if (savedData) {
      try {
        const parsed = JSON.parse(savedData);
        // Only load if saved within last 7 days (extended from 24 hours)
        if (Date.now() - parsed?.timestamp < 7 * 24 * 60 * 60 * 1000) {
          setFormData(parsed?.formData);
          setCurrentStep(parsed?.currentStep);
          if (parsed?.visitedSteps) {
            setVisitedSteps(new Set(parsed?.visitedSteps));
          }
        }
      } catch (error) {
        console.error('Error loading saved data:', error);
      }
    }
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      {/* Full-page submission overlay */}
      {isCreating && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl px-10 py-10 flex flex-col items-center gap-5 max-w-xs w-full mx-4">
            <div
              className="w-16 h-16 rounded-2xl flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg, #F58A54, #F06F6B)' }}
            >
              <svg className="animate-spin h-8 w-8 text-white" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
              </svg>
            </div>
            <div className="text-center">
              <h3 className="text-lg font-bold text-gray-900 mb-1">
                {isEditMode && !isDraftEditMode ? 'Saving Changes...' : 'Creating Your Stack...'}
              </h3>
              <p className="text-sm text-gray-500">
                {formData?.basics?.isOnlineOnly
                  ? 'Saving your options and setting up comparisons' :'Saving your stack and sending vendor requests'}
              </p>
            </div>
          </div>
        </div>
      )}
      {!isLoadingEdit && (
        <>
        {/* Gradient Hero Header */}
        <div
          className="relative overflow-hidden py-10 px-4 sm:px-6 lg:px-8"
          style={{ background: 'linear-gradient(to right, #F58A54, #F06F6B, #E0528A)' }}
        >
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-white/5 blur-3xl" />
            <div className="absolute -bottom-8 -left-8 w-32 h-32 rounded-full bg-white/5 blur-2xl" />
          </div>
          <div className="relative max-w-4xl mx-auto">
            <div className="flex items-center gap-4 mt-2">
              <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur-sm border border-white/30 flex items-center justify-center flex-shrink-0">
                <Icon name={isEditMode ? 'Edit' : 'Plus'} size={26} color="white" />
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-white">
                  {isEditMode ? `Edit Stack` : 'Create New Stack'}
                </h1>
                <p className="text-white/75 text-sm mt-0.5">
                  {isEditMode
                    ? `Editing: ${formData?.basics?.projectName || 'Stack'}`
                    : formData?.basics?.isOnlineOnly
                      ? 'Set up your purchase and compare buying options' :'Set up your purchase and send quote requests to vendors'}
                </p>
              </div>
            </div>

            {/* Step pills */}
            <div className="flex items-center gap-2 mt-5 flex-wrap">
              {steps?.map((step, idx) => (
                <button
                  key={idx}
                  onClick={() => handleStepNavigation(idx)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all duration-200 ${
                    idx === currentStep
                      ? 'bg-white text-primary shadow-md'
                      : visitedSteps?.has(idx)
                      ? 'bg-white/25 text-white border border-white/30' :'bg-white/10 text-white/60 border border-white/15'
                  }`}
                >
                  <Icon name={step?.icon} size={11} color={idx === currentStep ? '#F06F6B' : 'currentColor'} />
                  {step?.title}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Draft Save Status */}
          {draftSaveStatus && (
            <div className={`mb-6 p-4 rounded-2xl border ${
              draftSaveStatus === 'success' ?'bg-green-50 border-green-200' :'bg-red-50 border-red-200'
            }`}>
              <div className="flex items-center">
                <Icon
                  name={draftSaveStatus === 'success' ? 'CheckCircle' : 'AlertCircle'}
                  size={16}
                  className={`mr-2 ${draftSaveStatus === 'success' ? 'text-green-600' : 'text-red-600'}`}
                />
                <span className={`text-sm ${
                  draftSaveStatus === 'success' ? 'text-green-700' : 'text-red-700'
                }`}>
                  {draftSaveStatus === 'success' ? 'Draft saved successfully!' : 'Failed to save draft. Please try again.'}
                </span>
              </div>
            </div>
          )}

          {/* Main Content Card */}
          <div className="bg-card rounded-2xl border border-border shadow-sm">
            <div className="p-6 lg:p-8">
              {/* General Error */}
              {errors?.general && (
                <div className="mb-6 p-4 bg-error/10 border border-error/20 rounded-xl">
                  <div className="flex items-center">
                    <Icon name="AlertCircle" size={16} className="text-error mr-2" />
                    <span className="text-error text-sm">{errors?.general}</span>
                  </div>
                </div>
              )}

              {/* Step Content */}
              <div className="min-h-[400px]">
                {renderCurrentStep()}
              </div>

              {/* Navigation */}
              <StepNavigation
                currentStep={currentStep}
                totalSteps={steps?.length}
                onNext={handleNext}
                onPrevious={handlePrevious}
                onCancel={handleCancel}
                onSaveDraft={handleSaveDraft}
                onSaveAndExit={handleSaveAndExit}
                isNextDisabled={isNextDisabled()}
                isLoading={isCreating}
                isSavingDraft={isSavingDraft}
                nextButtonText={getNextButtonText()}
              />
            </div>
          </div>

          {/* Auto-save Indicator */}
          {!isEditMode && (
            <div className="mt-4 text-center">
              <p className="text-xs text-text-secondary flex items-center justify-center">
                <Icon name="Save" size={12} className="mr-1" />
                Your progress is automatically saved
              </p>
            </div>
          )}
        </div>
        </>
      )}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </div>
  );
};

export default CreateProjectFlow;